import { useCallback, useEffect, useMemo, useState } from "react";
import * as React from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import LocalSeeOutlinedIcon from "@mui/icons-material/LocalSeeOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { merge } from "lodash";

import ImageUpload from "components/image-upload";
import WebcamPicker from "components/webcam-picker";
import { getFileSizeError } from "utils/validations.utils";

import DynamicWebTwainModal from "../../features/dynamic-web-twain/dynamic-web-twain-modal.component";

type PhotoPickerProps = {
  open: boolean;
  isLoading: boolean;
  hasInitialImage: boolean;
  onClose: () => void;
  onSubmit: (files: File[]) => Promise<void>;
  canScan?: boolean;
  folderSettings?: {
    id: number;
    type: "patients";
    folderId: any;
    defaultFolderDestinationPath: string;
  };
  maxSizeLimit?: number;
};

export type PhotoPickerValues = {
  photos: File[];
  initialValue?: File;
};

const DEFAULT_VALUES: PhotoPickerValues = {
  photos: []
};

function getExtensionFromMime(type: string): string {
  if (type === "image/png") return "png";
  return "jpg";
}

export default function PhotoPicker({
  open,
  isLoading,
  hasInitialImage,
  onClose,
  onSubmit,
  canScan,
  folderSettings,
  maxSizeLimit = 3 * 1024 * 1024
}: PhotoPickerProps) {
  const theme = useTheme();
  const [step, setStep] = useState<"main" | "webcam">("main");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pasteHint, setPasteHint] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setErrorMessage(null);
      setPasteHint(null);
    }
  }, [open]);

  const defaultValues: PhotoPickerValues = useMemo(() => {
    return merge({}, DEFAULT_VALUES);
  }, []);

  const formMethods = useForm<PhotoPickerValues>({
    mode: "onChange",
    defaultValues
  });

  useEffect(() => {
    formMethods.reset(defaultValues);
  }, [defaultValues, formMethods]);

  const handleSubmit: SubmitHandler<{ photos: File[] }> = useCallback(
    async (values) => {
      await onSubmit(values.photos);
      formMethods.reset();
      onClose();
    },
    [onSubmit, formMethods, onClose]
  );

  const handleDelete = useCallback(async () => {
    await onSubmit?.([]);
    formMethods.reset();
    onClose();
  }, [onClose, onSubmit, formMethods]);

  const handleClose = useCallback(() => {
    onClose();
    setStep("main");
  }, [onClose]);

  const handlePastedFile = useCallback(
    (file: File) => {
      formMethods.setValue("photos", [file]);
    },
    [formMethods]
  );

  useEffect(() => {
    if (!open) return;

    const handlePaste = (e: ClipboardEvent) => {
      if (!e.clipboardData) return;

      for (const item of Array.from(e.clipboardData.items)) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            e.preventDefault();
            const ext = getExtensionFromMime(file.type);
            const namedFile = new File(
              [file],
              `insurance-card-${Date.now()}.${ext}`,
              { type: file.type }
            );
            handlePastedFile(namedFile);
          }
          return;
        }
      }
    };

    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [open, handlePastedFile]);

  const handleClipboardButtonClick = useCallback(async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        const imageType = item.types.find((t: string) =>
          t.startsWith("image/")
        );
        if (imageType) {
          const blob = await item.getType(imageType);
          const ext = getExtensionFromMime(imageType);
          const file = new File(
            [blob],
            `insurance-card-${Date.now()}.${ext}`,
            { type: imageType }
          );
          handlePastedFile(file);
          setPasteHint(null);
          return;
        }
      }
      setPasteHint("No image found on clipboard");
    } catch {
      setPasteHint(
        "Could not access clipboard. Press Ctrl/\u2318+V to paste instead."
      );
    }
  }, [handlePastedFile]);

  const photos = formMethods.watch("photos");
  useEffect(() => {
    if (photos?.length) {
      for (const photo of photos) {
        const error = getFileSizeError(photo.size, maxSizeLimit);
        if (error) {
          setErrorMessage(error);
          return;
        }
      }

      setErrorMessage(null);
    } else {
      setErrorMessage(null);
    }
  }, [maxSizeLimit, photos]);

  return (
    <Dialog open={open} onClose={handleClose}>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{
          width: "400px",
          p: 4
        }}
      >
        {step === "main" && (
          <>
            <Typography variant="h5" marginBottom={1} component="div">
              Photo
            </Typography>
            <Typography variant="body1" component="div">
              Take a picture using Webcam
            </Typography>
            <IconButton
              onClick={() => setStep("webcam")}
              size="large"
              sx={{
                backgroundColor: theme.palette.grey[100],
                mt: 2,
                p: 6
              }}
            >
              <LocalSeeOutlinedIcon />
            </IconButton>
            <Divider
              sx={{
                width: 150,
                m: 2
              }}
            >
              OR
            </Divider>
            {canScan && (
              <>
                <DynamicWebTwainModal
                  multiplePages={false}
                  title="Scan Document"
                  submitButtonText="Save"
                  onSave={(file) => {
                    formMethods.setValue("photos", [file]);
                  }}
                  folderSettings={folderSettings}
                />
                <Divider
                  sx={{
                    width: 150,
                    m: 2
                  }}
                >
                  OR
                </Divider>
              </>
            )}
            <FormProvider {...formMethods}>
              <ImageUpload
                name="photos"
                onRemove={handleDelete}
                hasInitialImage={hasInitialImage}
              />
            </FormProvider>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              or press Ctrl/⌘+V to paste
            </Typography>
            <Button
              variant="text"
              size="small"
              startIcon={<ContentPasteGoIcon />}
              onClick={handleClipboardButtonClick}
              sx={{ mt: 1, textTransform: "none" }}
              aria-label="Paste from Clipboard"
            >
              Paste from Clipboard
            </Button>
            {pasteHint && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {pasteHint}
              </Typography>
            )}
            <LoadingButton
              variant="contained"
              size="large"
              type="submit"
              loading={isLoading}
              sx={{
                marginTop: 2
              }}
              disabled={Boolean(errorMessage)}
              onClick={formMethods.handleSubmit(handleSubmit)}
            >
              Save
            </LoadingButton>
          </>
        )}
        {step === "webcam" && (
          <Box
            display="flex"
            flexDirection="column"
            sx={{
              minHeight: "500px"
            }}
          >
            <Box display="flex" alignItems="center" marginBottom={2}>
              <IconButton
                sx={{ marginRight: 1 }}
                onClick={() => setStep("main")}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" component="div" fontWeight={700}>
                Web Camera
              </Typography>
            </Box>
            <WebcamPicker
              container={{ width: 400 }}
              videoConstraints={{
                width: 512,
                height: 512
              }}
              onCapture={(file: File) => {
                setStep("main");
                formMethods.setValue("photos", [file]);
              }}
            />
          </Box>
        )}
        {Boolean(errorMessage) && (
          <Alert severity="error" sx={{ mt: 2, mb: -1 }}>
            {errorMessage}
          </Alert>
        )}
      </Box>
    </Dialog>
  );
}
